import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProductById } from '../../../services/products';
import { useCart } from '../../../context/CartContext';
import {
  getVariantStatus,
  isColorAgotadoForPrice,
  isPriceAgotadoForColor,
  priceLabel,
  findFirstAvailableSelection,
} from './stockUtils';
import './ProductDetail.css';

function formatPrice(value) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(value);
}

function DetailGallery({
  photos,
  productName,
  selectedPhoto,
  setSelectedPhoto,
  syncSelectionFromPhoto,
  showGlobalAgotadoBadge,
}) {
  const thumbsRef = useRef(null);
  const [thumbsOverflow, setThumbsOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateThumbScroll = useCallback(() => {
    const el = thumbsRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 2;
    setThumbsOverflow(overflow);
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateThumbScroll();
    const el = thumbsRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(updateThumbScroll);
    ro.observe(el);
    el.addEventListener('scroll', updateThumbScroll, { passive: true });
    window.addEventListener('resize', updateThumbScroll);
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', updateThumbScroll);
      window.removeEventListener('resize', updateThumbScroll);
    };
  }, [photos.length, updateThumbScroll]);

  useEffect(() => {
    const el = thumbsRef.current;
    if (!el) return;
    const active = el.querySelector('.detail-thumb.active');
    if (active) {
      active.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedPhoto]);

  const goToPhoto = (index) => {
    const next = (index + photos.length) % photos.length;
    setSelectedPhoto(next);
    syncSelectionFromPhoto(photos[next]);
  };

  const scrollThumbs = (dir) => {
    const el = thumbsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.7), behavior: 'smooth' });
  };

  const hasMany = photos.length > 1;

  return (
    <div className="detail-gallery">
      <div className="detail-main-image-wrap">
        {photos.length > 0 ? (
          <img
            key={photos[selectedPhoto].imagen}
            src={photos[selectedPhoto].imagen}
            alt={productName}
            className="detail-main-image"
          />
        ) : (
          <div className="detail-image-placeholder" />
        )}
        {showGlobalAgotadoBadge && (
          <span className="detail-badge">Agotado</span>
        )}
        {hasMany && (
          <>
            <button
              type="button"
              className="detail-nav-arrow detail-nav-arrow--prev"
              aria-label="Foto anterior"
              onClick={() => goToPhoto(selectedPhoto - 1)}
            >
              ‹
            </button>
            <button
              type="button"
              className="detail-nav-arrow detail-nav-arrow--next"
              aria-label="Foto siguiente"
              onClick={() => goToPhoto(selectedPhoto + 1)}
            >
              ›
            </button>
          </>
        )}
      </div>
      {hasMany && (
        <div
          className={`detail-thumbnails-wrap${thumbsOverflow ? ' detail-thumbnails-wrap--overflow' : ''}`}
        >
          {thumbsOverflow && (
            <button
              type="button"
              className="detail-thumbs-arrow detail-thumbs-arrow--prev"
              aria-label="Desplazar miniaturas a la izquierda"
              disabled={!canScrollLeft}
              onClick={() => scrollThumbs(-1)}
            >
              ‹
            </button>
          )}
          <div className="detail-thumbnails" ref={thumbsRef}>
            {photos.map((photo, i) => (
              <button
                key={photo.leora_foto_product_UUID}
                type="button"
                className={`detail-thumb ${i === selectedPhoto ? 'active' : ''}`}
                onClick={() => {
                  setSelectedPhoto(i);
                  syncSelectionFromPhoto(photo);
                }}
              >
                <img src={photo.imagen} alt={`${productName} ${i + 1}`} />
              </button>
            ))}
          </div>
          {thumbsOverflow && (
            <button
              type="button"
              className="detail-thumbs-arrow detail-thumbs-arrow--next"
              aria-label="Desplazar miniaturas a la derecha"
              disabled={!canScrollRight}
              onClick={() => scrollThumbs(1)}
            >
              ›
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProductById(id)
      .then((data) => {
        setProduct(data);
        const { price, color } = findFirstAvailableSelection(data);
        setSelectedPrice(price);
        setSelectedColor(color);
      })
      .catch((err) => console.error('Error cargando producto:', err))
      .finally(() => setLoading(false));
  }, [id]);

  const variantStatus = product
    ? getVariantStatus(product, selectedPrice, selectedColor)
    : 'disponible';

  if (loading) {
    return (
      <div className="detail-page">
        <p className="detail-loading">Cargando producto...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="detail-page">
        <p className="detail-loading">Producto no encontrado.</p>
        <Link to="/productos" className="detail-back">← Volver al catálogo</Link>
      </div>
    );
  }

  const photos = product.fotos || [];
  const precios = product.precios || [];
  const colores = product.colores || [];
  const stocks = product.stocks || [];
  const isAgotado = variantStatus === 'agotado';
  const isProximamente = variantStatus === 'proximamente';
  const showGlobalAgotadoBadge = product.estado === 'agotado';

  const syncSelectionFromPhoto = (photo) => {
    if (photo.precio_id) {
      const matchPrice = precios.find((p) => p.id === photo.precio_id);
      if (matchPrice) setSelectedPrice(matchPrice);
    }
    if (photo.color_id) {
      const matchColor = colores.find((c) => c.leora_colores_UUID === photo.color_id);
      if (matchColor) setSelectedColor(matchColor);
    }
    if (photo.stock_id) {
      const matchStock = stocks.find((s) => s.id === photo.stock_id);
      if (matchStock) {
        const matchPrice = precios.find((p) => p.id === matchStock.precio_id);
        const matchColor = colores.find(
          (c) => c.leora_colores_UUID === matchStock.color_id,
        );
        if (matchPrice) setSelectedPrice(matchPrice);
        if (matchColor) setSelectedColor(matchColor);
      }
    }
  };

  return (
    <div className="detail-page">
      <Link to="/productos" className="detail-back">← Volver al catálogo</Link>

      <div className="detail-container">
        <DetailGallery
          photos={photos}
          productName={product.nombre}
          selectedPhoto={selectedPhoto}
          setSelectedPhoto={setSelectedPhoto}
          syncSelectionFromPhoto={syncSelectionFromPhoto}
          showGlobalAgotadoBadge={showGlobalAgotadoBadge}
        />

        <div className="detail-info">
          <span className="detail-brand">{product.marca}</span>
          <h1 className="detail-name">{product.nombre}</h1>
          <span className="detail-category">{product.categoria}</span>

          <p className="detail-price">
            {selectedPrice
              ? formatPrice(selectedPrice.valor)
              : formatPrice(product.precio_minimo)}
          </p>

          {precios.length > 1 && (
            <div className="detail-options">
              <span className="detail-option-label">Presentación</span>
              <div className="detail-option-list">
                {precios.map((p) => {
                  const agotado = isPriceAgotadoForColor(
                    stocks,
                    p,
                    selectedColor,
                    product,
                  );
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={`detail-option-btn ${selectedPrice?.id === p.id ? 'active' : ''}`}
                      onClick={() => setSelectedPrice(p)}
                      title={agotado ? 'Agotado' : undefined}
                    >
                      {priceLabel(p, formatPrice)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {colores.length > 0 && (
            <div className="detail-options">
              <span className="detail-option-label">
                Color: {selectedColor?.nombre || ''}
              </span>
              <div className="detail-color-list">
                {colores.map((c) => {
                  const agotado = isColorAgotadoForPrice(
                    stocks,
                    selectedPrice,
                    c,
                    product,
                  );
                  const colorTitle =
                    c.nombre?.trim() || c.codigo_hex || 'Color';
                  return (
                    <button
                      key={c.leora_colores_UUID}
                      type="button"
                      className={`detail-color-btn ${selectedColor?.leora_colores_UUID === c.leora_colores_UUID ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedColor(c);
                        const matchIndex = photos.findIndex(
                          (p) => p.color_id === c.leora_colores_UUID,
                        );
                        if (matchIndex !== -1) setSelectedPhoto(matchIndex);
                      }}
                      title={agotado ? `${colorTitle} — Agotado` : colorTitle}
                      aria-label={
                        agotado ? `${colorTitle}, agotado` : colorTitle
                      }
                    >
                      <span
                        className="detail-color-swatch"
                        style={{ backgroundColor: c.codigo_hex }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {product.descripcion && (
            <div className="detail-description">
              <h3>Descripción</h3>
              <p>{product.descripcion}</p>
            </div>
          )}

          <button
            className="detail-add-btn"
            disabled={isAgotado || isProximamente}
            onClick={() => {
              if (isAgotado || isProximamente) return;
              addItem(product, selectedPrice, selectedColor, selectedPhoto);
              setAdded(true);
              setTimeout(() => setAdded(false), 1500);
            }}
          >
            {isProximamente
              ? 'Próximamente'
              : isAgotado
                ? 'Agotado'
                : added
                  ? '✓ Agregado'
                  : 'Agregar al carrito'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
